import * as THREE from 'three'
import { podeApoiarEmVeiculo } from '../../dominio/tabletop/apoioVeiculo'
import type { TipoObjetoTabletop } from '../../dominio/tabletop/tipos'

export interface IntersecaoSuperficie {
  ponto: THREE.Vector3
  normal: THREE.Vector3
  idVeiculo: string | null
}

/**
 * Calcula a rotação Euler [pitch, yaw, roll] de um objeto apoiado em uma superfície 3D.
 *
 * Regras acordadas no grill:
 * - Fogo e riscos mantêm sempre orientação vertical Y-up (chamas sobem contra a gravidade).
 * - Vítimas e ferramentas manuais inclinam acompanhando a normal da chapa metálica.
 * - Ao retornar para o solo plano (normal vertical), pitch e roll são zerados automaticamente,
 *   preservando o rumo/yaw da bússola.
 */
export function calcularRotacaoSuperficie(
  tipo: TipoObjetoTabletop,
  normalMundo: THREE.Vector3,
  yawAtualRad = 0,
): [number, number, number] {
  // Fogo e riscos operacionais sempre queimam/emanam verticalmente para cima (+Y)
  if (tipo === 'fogo' || tipo === 'caboEletrico' || tipo === 'derramamento') {
    return [0, yawAtualRad, 0]
  }

  // Se a normal for essencialmente plana/vertical, zera pitch e roll (nivelamento de piso)
  if (normalMundo.y >= 0.999) {
    return [0, yawAtualRad, 0]
  }

  // Alinha o vetor UP (0, 1, 0) com a normal da lataria, preservando a guinada (yaw)
  const normalNormalizada = normalMundo.clone().normalize()
  const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawAtualRad)
  const qTilt = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normalNormalizada)
  const qTotal = qTilt.multiply(qYaw)
  const euler = new THREE.Euler().setFromQuaternion(qTotal, 'YXZ')

  return [euler.x, euler.y, euler.z]
}

/**
 * Realiza o raycasting inteligente na cena do Tabletop:
 * Se o objeto for do tipo apoiável (risco, ferramenta, vítima) e houver veículos,
 * colide com a malha da carroceria do veículo; caso contrário, colide com o solo (Y = 0).
 */
export function buscarIntersecaoSuperficie(
  raycaster: THREE.Raycaster,
  veiculosMap: Map<string, THREE.Object3D>,
  planoChao: THREE.Plane,
  tipoObjeto?: TipoObjetoTabletop,
): IntersecaoSuperficie | null {
  if (tipoObjeto && podeApoiarEmVeiculo(tipoObjeto) && veiculosMap.size > 0) {
    const grupos = Array.from(veiculosMap.values())
    const intersecoes = raycaster.intersectObjects(grupos, true)

    for (const hit of intersecoes) {
      if (!(hit.object instanceof THREE.Mesh)) continue
      // Ignora anéis de seleção, marcadores auxiliares e objetos quase colados ao solo
      if (hit.point.y < 0.08) continue
      if (hit.object.geometry instanceof THREE.RingGeometry) continue
      if (hit.object.material && 'opacity' in hit.object.material && hit.object.material.opacity === 0) continue

      let idVeiculo: string | null = null
      for (const [id, grp] of veiculosMap.entries()) {
        let atual: THREE.Object3D | null = hit.object
        while (atual) {
          if (atual === grp) {
            idVeiculo = id
            break
          }
          atual = atual.parent
        }
        if (idVeiculo) break
      }

      let normalMundo = new THREE.Vector3(0, 1, 0)
      if (hit.face) {
        normalMundo = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize()
      }

      return {
        ponto: hit.point,
        normal: normalMundo,
        idVeiculo,
      }
    }
  }

  const pontoSolo = raycaster.ray.intersectPlane(planoChao, new THREE.Vector3())
  if (!pontoSolo) return null

  return {
    ponto: pontoSolo,
    normal: new THREE.Vector3(0, 1, 0),
    idVeiculo: null,
  }
}
